import { useState, type KeyboardEvent, type PointerEvent, type ReactNode } from 'react';
import { boundedTone, level, type Tone } from './state.ts';
import { NO_VALUE } from './format.ts';
import { axisClock } from './timeScale.ts';
import styles from './Sparkline.module.css';

/* ==========================================================================
   Sparkline — a 1px trace on a fixed scale, hand-drawn as SVG.

   No area fill, no gradient, no drop shadow, no rounded caps, no draw-in
   animation. New data appears where it belongs. A gap in the series is a gap
   in the line, not an interpolation across missing readings.

   Three rules this primitive enforces so a caller cannot get them wrong.

   1 · THE BOX IS THE METRIC'S RANGE, NOT THE WINDOW'S.
       A percentage is 0–100 by definition, so that is the y-domain and the
       trace's HEIGHT IN THE BOX IS THE READING. An 8% battery sits in the
       bottom eighth; a 90% battery sits at the top; the two never draw the
       same picture. Autoscaling to the local extent — which is what a chart
       library does by default — makes a dying battery and a healthy one
       identical diagonals, and turns the only time-series primitive in the
       console into the least trustworthy display on the screen.

       An unbounded quantity (speed, temperature, bytes) has no such range;
       pass `min`/`max` if one exists, otherwise the trace fits its window and
       says so — the floor rule goes dotted instead of solid, and the box's
       own label names the fitted extent. A fitted box never pretends to be
       absolute.

   2 · COLOUR ANSWERS THE THRESHOLD, NOT THE CATEGORY.
       Give it `warn`/`bad` (or a bare `threshold`) and it resolves its own
       tone off the LATEST reading through exactly the function `Meter` uses —
       `--nominal` steel inside limits, amber at warn, red past bad. A literal
       `tone` is a floor honoured only inside the limits, because a green
       trace beside a red meter reading the same battery is the console lying
       to the operator.

       The hard limit is also DRAWN, as a 1px dashed rule at its true y — grey
       while the reading is inside it, and in `--warn`/`--error` only once it
       is actually crossed. A trace with nothing marking 20% asks the operator
       to know where 20% is; a trace with the rule on it answers the question;
       a trace that paints that rule red while the battery reads 80% has spent
       the alarm colour on a non-event.

   3 · IT ANSWERS "WHAT WAS IT AT 22:41Z".
       Hover (or focus and arrow) drops a 1px `--plot-crosshair` playhead on
       the nearest sample and prints that sample's value against its Zulu
       timestamp in a two-column chip. Without that a plot is a picture of a
       trend; with it, it is an instrument.
   ========================================================================== */

type TimeInput = number | string | null | undefined;

interface SparklineProps {
  /** Oldest → newest. `null` breaks the line rather than reading as zero. */
  values: readonly (number | null | undefined)[];
  /**
   * Epoch ms or ISO strings, aligned index-for-index with `values`. Supply it
   * and the crosshair readout carries a real Zulu timestamp instead of a
   * sample ordinal — which is the difference between "it fell" and "it was
   * 79.9% at 22:41:53Z".
   */
  times?: readonly TimeInput[] | undefined;
  width?: number | undefined;
  height?: number | undefined;
  /**
   * Thresholds in the value's own units. Low-is-bad by default (battery, disk
   * free, signal); pass `invert` for high-is-bad (CPU, temperature). Supplying
   * either one makes the trace self-colouring and draws the limit rules.
   */
  warn?: number | undefined;
  bad?: number | undefined;
  invert?: boolean | undefined;
  /** A single hard limit: drawn in `--error`, and absent `warn`/`bad` it acts
   *  as both — past it is red. */
  threshold?: number | undefined;
  /** Honoured only while the reading is inside its limits. */
  tone?: Tone | undefined;
  /**
   * The y-domain. Omit for a percentage (`unit="%"`) and it is 0–100, which
   * is what a percentage means. Omit for anything else and the trace fits its
   * own window and declares that it did.
   */
  min?: number | undefined;
  max?: number | undefined;
  /** Dot on the newest reading — it is the value the number beside it shows. */
  head?: boolean | undefined;
  /** Print the latest value ahead of the trace: `80% ▁▂▃▅▇`. */
  readout?: boolean | undefined;
  /** Names the quantity where the trace's subject is not already on the line. */
  caption?: string | undefined;
  unit?: string | undefined;
  digits?: number | undefined;
  label?: string | undefined;
  /**
   * Crosshair + readout chip. On by default. Turn it off for a trace inside a
   * dense repeating row, where a hover chip would fight the row hover.
   */
  probe?: boolean | undefined;
}

/** Resolve the tone a series is entitled to, from its own last reading. */
function traceTone(
  last: number | null,
  warn: number | undefined,
  bad: number | undefined,
  threshold: number | undefined,
  invert: boolean,
  fallback: Tone | undefined,
): Tone {
  const w = warn ?? bad ?? threshold;
  const b = bad ?? threshold ?? warn;
  if (w === undefined || b === undefined) return fallback ?? 'nominal';
  /* `boundedTone`, so the rule is written once: the caller's tone is a floor
     honoured inside the limits, and a crossed threshold always wins. */
  return boundedTone(level(last, w, b, invert), fallback);
}

function epoch(t: TimeInput): number | null {
  if (t == null) return null;
  const ms = typeof t === 'number' ? t : Date.parse(t);
  return Number.isNaN(ms) ? null : ms;
}

export function Sparkline({
  values,
  times,
  width = 64,
  height = 16,
  warn,
  bad,
  invert = false,
  threshold,
  tone,
  min,
  max,
  head = false,
  readout = false,
  caption,
  unit = '',
  digits = 0,
  label,
  probe = true,
}: SparklineProps) {
  const [cursor, setCursor] = useState<number | null>(null);

  const pts = values.map((v) => (v == null || Number.isNaN(v) ? null : v));
  const known = pts.filter((v): v is number => v !== null);
  const n = pts.length;

  const lastIdx = (() => {
    for (let i = n - 1; i >= 0; i -= 1) if (pts[i] !== null) return i;
    return -1;
  })();
  const lastVal = lastIdx >= 0 ? (pts[lastIdx] as number) : null;

  const resolved = traceTone(lastVal, warn, bad, threshold, invert, tone);

  /* ---- domain -----------------------------------------------------------
     A percentage is 0–100. Anything else takes the caller's bounds, and only
     falls back to the window's own extent when neither exists — in which case
     the box says so rather than passing a relative picture off as absolute. */
  const dataLo = known.length > 0 ? Math.min(...known) : 0;
  const dataHi = known.length > 0 ? Math.max(...known) : 1;
  const isPct = unit === '%';
  const loRaw = min ?? (isPct ? 0 : undefined);
  const hiRaw = max ?? (isPct ? 100 : undefined);
  const fitted = loRaw === undefined || hiRaw === undefined;
  const lo = loRaw ?? dataLo;
  const hiFit = hiRaw ?? dataHi;
  const hi = hiFit === lo ? lo + Math.max(1, Math.abs(lo) * 0.02) : hiFit;

  /* Inset by the head-dot radius so an extreme reading is not clipped in half.
     yBot is the domain floor, yTop the ceiling: the trace's height in this
     band IS the reading, which is the whole point of a fixed domain. */
  const padY = 1.5;
  const yTop = padY;
  const yBot = height - padY;
  const x = (i: number) => (n <= 1 ? width / 2 : (i / (n - 1)) * (width - 1) + 0.5);
  const y = (v: number) => {
    const t = (v - lo) / (hi - lo);
    return yBot - Math.max(0, Math.min(1, t)) * (yBot - yTop);
  };
  const inDomain = (v: number) => v > lo && v < hi;

  // Break the path wherever a reading is missing.
  const segments: string[] = [];
  let cur: string[] = [];
  pts.forEach((v, i) => {
    if (v === null) {
      if (cur.length > 1) segments.push(cur.join(' '));
      cur = [];
      return;
    }
    cur.push(`${cur.length === 0 ? 'M' : 'L'}${x(i).toFixed(2)},${y(v).toFixed(2)}`);
  });
  if (cur.length > 1) segments.push(cur.join(' '));

  /* Which limits to draw, and whether the newest reading has crossed one. A
     crossed rule brightens: the trace has already gone red, and the rule says
     what it went red against. */
  const hardBad = bad ?? threshold;
  const softWarnRaw = warn ?? (bad === undefined ? threshold : undefined);
  /* Two rules the eye cannot separate are one rule that lies about where the
     limits are. When warn and bad land within 3.5px of each other — a dashed
     1px pair needs about that much air to read as two — only the hard limit
     is drawn. */
  const softWarn =
    softWarnRaw !== undefined &&
    hardBad !== undefined &&
    Math.abs(y(softWarnRaw) - y(hardBad)) < 3.5
      ? undefined
      : softWarnRaw;
  const crossed = (limit: number | undefined) =>
    limit !== undefined && lastVal !== null && (invert ? lastVal >= limit : lastVal <= limit);

  const timeAt = (i: number) => (times === undefined ? null : epoch(times[i]));
  const active = probe && n > 1 && known.length > 0 ? cursor : null;

  const idxFromX = (clientX: number, rect: DOMRect) => {
    if (n <= 1) return 0;
    const f = Math.max(0, Math.min(1, (clientX - rect.left - 0.5) / Math.max(1, rect.width - 1)));
    return Math.round(f * (n - 1));
  };
  const onMove = (e: PointerEvent<HTMLSpanElement>) => {
    setCursor(idxFromX(e.clientX, e.currentTarget.getBoundingClientRect()));
  };
  const onKey = (e: KeyboardEvent<HTMLSpanElement>) => {
    if (n === 0) return;
    const at = cursor ?? lastIdx;
    if (e.key === 'ArrowLeft') { setCursor(Math.max(0, at - 1)); e.preventDefault(); }
    else if (e.key === 'ArrowRight') { setCursor(Math.min(n - 1, at + 1)); e.preventDefault(); }
    else if (e.key === 'Home') { setCursor(0); e.preventDefault(); }
    else if (e.key === 'End') { setCursor(n - 1); e.preventDefault(); }
    else if (e.key === 'Escape') setCursor(null);
  };

  const fmt = (v: number) => `${v.toFixed(digits)}${unit}`;
  /* The inline number is a glance and rounds; the crosshair is the instrument
     and does not. Printing `80%` where the table beside it says `79.9` is the
     small lie that costs a display its credibility. */
  const precise = (v: number) => {
    const s = v.toFixed(Math.max(digits, 2));
    return s.includes('.') ? s.replace(/0+$/, '').replace(/\.$/, '') : s;
  };
  /* The one sentence that says what this box is: what it reads now, over how
     many samples, against what scale. A fitted box never claims otherwise. */
  const describe = [
    label ?? caption ?? 'series',
    lastVal === null ? 'no readings' : fmt(lastVal),
    known.length > 0
      ? fitted
        ? `${known.length} readings, scale fitted ${fmt(dataLo)}–${fmt(dataHi)}`
        : `${known.length} readings, scale ${fmt(lo)}–${fmt(hi)}`
      : '',
  ].filter(Boolean).join(' · ');

  const rule = (limit: number | undefined, kind: 'warn' | 'error') =>
    limit === undefined || !inDomain(limit) ? null : (
      <line
        className={styles.limit}
        data-limit={kind}
        data-crossed={crossed(limit)}
        x1={0}
        x2={width}
        y1={y(limit)}
        y2={y(limit)}
      />
    );

  const cursorVal = active === null ? null : pts[active] ?? null;

  const svg = (
    <svg
      className={styles.svg}
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      aria-hidden="true"
      focusable="false"
    >
      {/* The domain floor. Solid when the box is the metric's real range,
          dotted when it is only this window's — so "where is this in its
          range" is never answered by a scale that moved. */}
      <line
        className={styles.floor}
        data-fitted={fitted}
        x1={0}
        x2={width}
        y1={yBot}
        y2={yBot}
      />
      {/* The hard limit is always published; the soft one only once it is the
          live threshold. Two rules in a 14px box is one too many while the
          reading is comfortably above both. */}
      {crossed(softWarn) ? rule(softWarn, 'warn') : null}
      {rule(hardBad, 'error')}
      {segments.length === 0 ? (
        <line className={styles.empty} x1={0} x2={width} y1={height / 2} y2={height / 2} />
      ) : (
        segments.map((d) => <path key={d} className={styles.line} d={d} />)
      )}
      {head && lastVal != null && (
        <circle className={styles.head} cx={x(lastIdx)} cy={y(lastVal)} r={1.5} />
      )}
      {active !== null && (
        <line className={styles.crosshair} x1={x(active)} x2={x(active)} y1={0} y2={height} />
      )}
      {active !== null && cursorVal != null && (
        <circle className={styles.marker} cx={x(active)} cy={y(cursorVal)} r={2} />
      )}
    </svg>
  );

  const chip =
    active === null ? null : (
      <span className={styles.chip} role="status">
        <span className={styles.chipKey}>
          {(() => {
            const t = timeAt(active);
            if (t !== null) return axisClock(t);
            return `${active + 1}/${n}`;
          })()}
        </span>
        <span className={styles.chipVal}>
          {cursorVal == null ? NO_VALUE : precise(cursorVal)}
          {cursorVal != null && unit !== '' && <span className={styles.unit}>{unit}</span>}
        </span>
      </span>
    );

  const box = (
    <span
      className={styles.hit}
      data-tone={resolved}
      style={{ width: `${width}px`, height: `${height}px` }}
      title={describe}
      {...(probe
        ? {
          tabIndex: 0,
          role: 'group',
          'aria-label': describe,
          onPointerMove: onMove,
          onPointerLeave: () => setCursor(null),
          onKeyDown: onKey,
          onBlur: () => setCursor(null),
        }
        : { role: 'img', 'aria-label': describe })}
    >
      {svg}
      {chip}
    </span>
  );

  if (!readout) return box;

  return (
    <span className={styles.inline} data-tone={resolved}>
      {caption !== undefined && <span className={styles.caption}>{caption}</span>}
      <span className={styles.value} data-toned={resolved !== 'nominal' && resolved !== 'neutral'}>
        {lastVal == null ? NO_VALUE : lastVal.toFixed(digits)}
        {lastVal != null && unit !== '' && <span className={styles.unit}>{unit}</span>}
      </span>
      {box}
    </span>
  );
}

/**
 * `81% ▁▂▃▅▇` with a caller-supplied left half — use it when the value is
 * already formatted (a fraction, a rate, a byte count). When the left half is
 * just the number, `<Sparkline readout>` says the same thing in one element.
 */
export function TrendValue({
  children,
  values,
  times,
  tone,
  warn,
  bad,
  invert,
  threshold,
  min,
  max,
  unit,
  digits,
  width,
  height,
  label,
}: {
  children: ReactNode;
  values: readonly (number | null | undefined)[];
  times?: readonly TimeInput[] | undefined;
  tone?: Tone | undefined;
  warn?: number | undefined;
  bad?: number | undefined;
  invert?: boolean | undefined;
  threshold?: number | undefined;
  min?: number | undefined;
  max?: number | undefined;
  unit?: string | undefined;
  digits?: number | undefined;
  width?: number | undefined;
  height?: number | undefined;
  label?: string | undefined;
}) {
  return (
    <span className={styles.inline}>
      {children}
      <Sparkline
        values={values}
        times={times}
        tone={tone}
        warn={warn}
        bad={bad}
        invert={invert}
        threshold={threshold}
        min={min}
        max={max}
        unit={unit}
        digits={digits}
        label={label}
        width={width ?? 48}
        height={height ?? 12}
      />
    </span>
  );
}
